import { TargetSelector } from "@client/selectors";
import { type BaseAppState, type EntityRef } from "@client/types";
import { type StyleSheetTextType } from "@client/types/StyleSheet";
import { type WithClassName } from "@common/declarations";
import classNames from "classnames";
import React from "react";
import { useSelector } from "react-redux";
import {
  ContentH1HeadingStyle,
  ContentH2HeadingStyle,
  ContentH3HeadingStyle,
  ContentH4HeadingStyle,
  ContentH5HeadingStyle,
  ContentH6HeadingStyle
} from "../Style/Heading";
import ContentTextStyle from "../Style/Text";
import { makeStyleSheetTextClassNameSelector } from "./Text/selectors";

export default <TProps extends {}>(
  Cmp: React.ComponentType<TProps>,
  styleSheets: StyleSheetTextType[],
  prefix?: string
) => {
  const selector = makeStyleSheetTextClassNameSelector(TargetSelector.Entity, styleSheets, prefix);

  type Props = TProps &
    EntityRef &
    Partial<WithClassName> & {
      /** applies to text CSS only */
      textSubSelector?: string;
    };
  return ({ textSubSelector, ...props }: Props) => {
    const classNameArgs = React.useMemo(() => ({ id: props.id, source: props.source }), [props.id, props.source]);

    const className = useSelector<BaseAppState, string>((state) => selector(state, classNameArgs));

    return (
      <>
        {styleSheets.includes("text") && (
          <ContentTextStyle prefix={prefix} id={props.id} textSubSelector={textSubSelector} source={props.source} />
        )}
        {styleSheets.includes("h1") && (
          <ContentH1HeadingStyle prefix={prefix} id={props.id} source={props.source} textSubSelector="h1" />
        )}
        {styleSheets.includes("h2") && (
          <ContentH2HeadingStyle prefix={prefix} id={props.id} source={props.source} textSubSelector="h2" />
        )}
        {styleSheets.includes("h3") && (
          <ContentH3HeadingStyle prefix={prefix} id={props.id} source={props.source} textSubSelector="h3" />
        )}
        {styleSheets.includes("h4") && (
          <ContentH4HeadingStyle prefix={prefix} id={props.id} source={props.source} textSubSelector="h4" />
        )}
        {styleSheets.includes("h5") && (
          <ContentH5HeadingStyle prefix={prefix} id={props.id} source={props.source} textSubSelector="h5" />
        )}
        {styleSheets.includes("h6") && (
          <ContentH6HeadingStyle prefix={prefix} id={props.id} source={props.source} textSubSelector="h6" />
        )}

        <Cmp {...(props as TProps)} className={classNames(props.className, className)} />
      </>
    );
  };
};
